import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ArrowUpRight, CheckCircle2, Mail } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSubmitted(true);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[88vh] flex items-center justify-center px-6 py-20">
      <div className="w-full max-w-md">
        <div className="tag mb-4">// recover access</div>
        <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tighter mb-3">
          Forgot password
        </h1>
        <p className="text-zinc-400 mb-8 text-sm">
          Drop your email — if it&apos;s on our records, we&apos;ll send a
          single-use reset link (expires in 1 hour).
        </p>

        {submitted ? (
          <div
            className="border border-[var(--primary)]/40 bg-[var(--primary)]/5 p-6"
            data-testid="forgot-password-success"
          >
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 border border-[var(--primary)]/60 bg-[var(--primary)]/10 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="text-[var(--primary)]" size={18} strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-heading font-bold text-lg mb-1">Check your inbox</h3>
                <p className="text-zinc-300 text-sm leading-relaxed">
                  If <span className="text-white font-mono">{email}</span> matches an account, a reset
                  link is on its way. The link is valid for <strong>1 hour</strong> and can only be used once.
                </p>
                <Link to="/login" className="inline-flex items-center gap-2 text-[var(--primary)] hover:text-white text-xs font-mono uppercase tracking-widest mt-4">
                  Back to sign in <ArrowUpRight size={14} />
                </Link>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5" data-testid="forgot-password-form">
            <div>
              <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">
                Email
              </label>
              <div className="relative">
                <Mail
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
                  strokeWidth={1.5}
                />
                <input
                  required
                  type="email"
                  data-testid="forgot-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-gold pl-10"
                  placeholder="you@artist.com"
                />
              </div>
            </div>

            {err && (
              <div className="border border-red-500/40 bg-red-500/10 text-red-300 text-sm px-4 py-3">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="forgot-submit"
              className="btn-primary w-full justify-center"
            >
              {loading ? "Sending…" : "Send reset link"} <ArrowUpRight size={16} />
            </button>

            <div className="text-xs text-zinc-500 text-center">
              Remembered it?{" "}
              <Link to="/login" className="text-[var(--primary)] hover:underline">
                Sign in
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
